<template>
    <div class="page-modal">
        <widgetModal title="Edit Contact Tags" :subtitle="contact ? `${contact.first_name} ${contact.last_name}` : ''"
            btnlefttext="Save Changes" btncentertext="Cancel" :leftloading="isSubmitting" :centerloading="false"
            :footerhidden="false" :hiderightbtn="true" footer_padding="10px 18px 30px 30px"
            header_padding="30px 0px 0px 31px" body_padding="0px 30px" width="500px" @onLeftClick="saveChanges"
            @onCenterClick="onCancel">
            <div class="widget-modal-body-noscoll">
                <div class="user-info" v-if="contact">
                    <div class="user-avatar">
                        <img src="@/assets/contact-avatar.svg" alt="Contact Avatar">
                    </div>
                    <div class="user-details">
                        <h4>{{ contact.first_name }} {{ contact.last_name }}</h4>
                        <p>{{ contact.email }}</p>
                        <p v-if="contact.position">{{ contact.position }}</p>
                    </div>
                </div>

                <div class="tags-section">
                    <h4>Current Tags</h4>
                    <div class="current-tags" v-if="contact && contact.tags && contact.tags.length > 0">
                        <div class="tag-item" v-for="(tag, index) in editableTags" :key="index">
                            <input v-model="tag.name" @input="onTagEdit(tag, index)" class="tag-input" type="text"
                                :placeholder="'Tag ' + (index + 1)" />
                            <button @click="removeTag(index)" class="remove-btn" type="button">
                                <img src="@/assets/admin-icon/cross-icon.svg" alt="Remove">
                            </button>
                        </div>
                    </div>
                    <div v-else class="no-tags">
                        <p>No tags found for this contact</p>
                    </div>

                    <div class="add-new-tag">
                        <h4>Add New Tags</h4>
                        <widgetTags v-model="newTags" input_width="100%" placeholder="Add new tags"></widgetTags>
                    </div>
                </div>
            </div>
        </widgetModal>
    </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: "edit-contact-tags-modal",
    props: {
        contact: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            editableTags: [],
            newTags: {
                added: [],
                removed: []
            },
            isSubmitting: false,
        };
    },
    mounted() {
        this.initializeTags();
    },
    methods: {
        ...mapActions(['CONTACT_UPDATE_TAGS']),

        initializeTags() {
            // Create a copy of existing tags for editing
            if (this.contact && this.contact.tags) {
                this.editableTags = this.contact.tags.map(tag => ({
                    id: tag.id,
                    name: tag.name,
                    originalName: tag.name,
                    isModified: false
                }));
            }
        },

        onTagEdit(tag, index) {
            // Mark tag as modified if the name changed
            tag.isModified = tag.name !== tag.originalName;
        },

        removeTag(index) {
            const removedTag = this.editableTags.splice(index, 1)[0];
            // If it was an existing tag, track it for removal
            if (removedTag.id) {
                this.newTags.removed.push(removedTag.id);
            }
        },

        async saveChanges() {
            this.isSubmitting = true;

            try {
                // Prepare the payload
                const payload = {
                    contact_id: this.contact.id,
                    updated_tags: this.editableTags.filter(tag => tag.isModified && tag.name.trim() !== ''),
                    new_tags: this.newTags.added,
                    removed_tags: this.newTags.removed
                };

                // Call the API
                const response = await this.CONTACT_UPDATE_TAGS(payload);

                this.$notify({
                    group: "status",
                    title: "Success",
                    text: "Contact tags updated successfully",
                    type: "success",
                    duration: 3000,
                });

                // Emit success event with updated data
                this.$emit('onTagsUpdated', response.data);
                this.$emit('onCancel'); // Close modal

            } catch (error) {
                console.error('Error updating tags:', error);
                this.$notify({
                    group: "status",
                    title: "Error",
                    text: "Failed to update contact tags",
                    type: "warn",
                    duration: 3000,
                });
            } finally {
                this.isSubmitting = false;
            }
        },

        onCancel() {
            this.$emit('onCancel');
        },
    },
};
</script>

<style lang="scss" scoped>
$assets: "~@/assets";

.page-modal {
    .user-info {
        display: flex;
        align-items: center;
        margin-bottom: 30px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 10px;

        .user-avatar {
            margin-right: 15px;

            img {
                width: 60px;
                height: 60px;
                border-radius: 50%;
                object-fit: cover;
            }
        }

        .user-details {
            h4 {
                margin: 0 0 5px 0;
                font-size: 18px;
                color: #333;
            }

            p {
                margin: 0 0 3px 0;
                font-size: 14px;
                color: #666;
            }
        }
    }

    .tags-section {
        h4 {
            margin: 0 0 15px 0;
            font-size: 16px;
            color: #333;
        }

        .current-tags {
            margin-bottom: 30px;

            .tag-item {
                display: flex;
                align-items: center;
                margin-bottom: 10px;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 5px;
                background: #fff;

                .tag-input {
                    flex: 1;
                    border: none;
                    outline: none;
                    padding: 8px 12px;
                    font-size: 14px;
                    border-radius: 6px;

                    &:focus {
                        background: #f0f8ff;
                    }
                }

                .remove-btn {
                    background: #ff4757;
                    border: none;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    margin-left: 8px;

                    &:hover {
                        background: #ff3838;
                    }

                    img {
                        width: 12px;
                        height: 12px;
                        filter: brightness(0) invert(1);
                    }
                }
            }
        }

        .no-tags {
            text-align: center;
            padding: 20px;
            color: #999;
            font-style: italic;
            margin-bottom: 30px;
        }

        .add-new-tag {
            h4 {
                margin-bottom: 10px;
                color: #1BA5A4;
            }
        }
    }
}
</style>